import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CircularProgress, Checkbox, Menu, MenuItem, TextField, Tooltip } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import WarningIcon from "@mui/icons-material/Warning";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DoNotDisturbIcon from "@mui/icons-material/DoNotDisturb";
import BuildCircleIcon from "@mui/icons-material/BuildCircle";
import dayjs from "dayjs";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import {
	selectedClassAtom,
	unsavedChangesAtom,
	spotOrderingSelectionModelAtom,
} from "../../../../components/Atoms/atoms";
import { getSelectedIdsFromModel } from "./selectionModel";
import SubdirectoryArrowRightIcon from "@mui/icons-material/SubdirectoryArrowRight";
import SwapVertIcon from "@mui/icons-material/SwapVert";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import BuildIcon from "@mui/icons-material/Build";
import SchoolIcon from '@mui/icons-material/School';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import BadgeIcon from '@mui/icons-material/Badge';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import EventIcon from '@mui/icons-material/Event';

const ROWS_PER_PAGE = 100;

const DEFAULT_COLUMN_VISIBILITY_MODEL = {
	classid: true,
	manageAction: true,
	ctitle: true,
	csize: true,
	form: false,
	name: true,
	taOrPI: true,
	evaluate: true,
	c_location: false,
	c_edate: true,
	examdate: true,
	survey_start: false,
	survey_end: false,
};

function getInstructorNamesText(row) {
	if (!Array.isArray(row?.roles) || row.roles.length === 0) {
		return row?.name || "";
	}

	return row.roles
		.map((role) => {
			if (typeof role?.name === "string" && role.name.trim()) {
				return role.name.trim();
			}

			const lastName = role?.user?.last_name?.trim();
			const firstName = role?.user?.first_name?.trim();
			return [lastName, firstName].filter(Boolean).join(", ");
		})
		.filter(Boolean)
		.join(", ");
}

function getRoleFilterText(row) {
	if (!Array.isArray(row?.roles) || row.roles.length === 0) {
		return "N/A No Roles";
	}

	const roleNames = row.roles
		.map((role) => role.class_role)
		.filter(Boolean)
		.join(", ");
	if (row.roles.every((role) => role.class_role !== "TA")) {
		return `INST ${roleNames}`.trim();
	}

	if (row.roles.every((role) => role.class_role === "TA")) {
		return `TA ${roleNames}`.trim();
	}

	return `Mix ${roleNames}`.trim();
}

function normalizeFilterValue(value) {
	return String(value ?? "")
		.trim()
		.toLowerCase();
}

function getColumnSortValue(row, column) {
	if (!column) {
		return "";
	}

	if (typeof column.getSortValue === "function") {
		return column.getSortValue(row);
	}

	if (typeof column.getValue === "function") {
		return column.getValue(row);
	}

	return row?.[column.field];
}

function getColumnFilterValue(row, column) {
	if (!column) {
		return "";
	}

	if (typeof column.getFilterValue === "function") {
		return column.getFilterValue(row);
	}

	const value = getColumnSortValue(row, column);
	if (Array.isArray(value)) {
		return value.join(", ");
	}

	return value ?? "";
}

function rowMatchesColumnFilters(row, columnFilters, columnsByField) {
	return Object.entries(columnFilters).every(([field, filterValue]) => {
		const normalizedFilterValue = normalizeFilterValue(filterValue);
		if (!normalizedFilterValue) {
			return true;
		}

		const column = columnsByField.get(field);
		const candidateValue = normalizeFilterValue(getColumnFilterValue(row, column));
		return candidateValue.includes(normalizedFilterValue);
	});
}

function filterGroupedRows(rows, columnFilters, columnsByField) {
	const activeFilters = Object.entries(columnFilters).filter(([, value]) =>
		normalizeFilterValue(value),
	);
	if (activeFilters.length === 0) {
		return rows;
	}

	const groupsByRootId = new Map();
	rows.forEach((row) => {
		const rootId = row._groupRootId ?? row.classid;
		if (!groupsByRootId.has(rootId)) {
			groupsByRootId.set(rootId, []);
		}

		groupsByRootId.get(rootId).push(row);
	});

	return Array.from(groupsByRootId.values()).flatMap((groupRows) => {
		const matchingRows = groupRows.filter((row) =>
			rowMatchesColumnFilters(row, columnFilters, columnsByField),
		);

		if (matchingRows.length === 0) {
			return [];
		}

		const rootRow =
			groupRows.find((row) => (row._groupRootId ?? row.classid) === row.classid) ?? groupRows[0];
		const includedClassIds = new Set(matchingRows.map((row) => row.classid));

		if (matchingRows.some((row) => row.classid !== rootRow.classid)) {
			includedClassIds.add(rootRow.classid);
		}

		return groupRows.filter((row) => includedClassIds.has(row.classid));
	});
}

function compareCtitles(leftTitle, rightTitle) {
	const parseCtitle = (title) => {
		const normalizedTitle = String(title || "");
		const parts = normalizedTitle.split(" ");
		if (parts.length === 5) {
			return [parts[0] + " " + parts[1], parts[2], parts[3], parts[4]];
		}

		return parts;
	};

	const leftParts = parseCtitle(leftTitle);
	const rightParts = parseCtitle(rightTitle);
	const maxLength = Math.max(leftParts.length, rightParts.length);

	for (let index = 0; index < maxLength; index += 1) {
		const leftPart = leftParts[index] ?? "";
		const rightPart = rightParts[index] ?? "";

		if (leftPart < rightPart) {
			return -1;
		}

		if (leftPart > rightPart) {
			return 1;
		}
	}

	return 0;
}

function compareEvaluateValues(leftValue, rightValue) {
	const order = ["Yes", "Pending", "Caution", "No", null, ""];
	const leftIndex = order.indexOf(leftValue);
	const rightIndex = order.indexOf(rightValue);
	const safeLeftIndex = leftIndex === -1 ? order.length : leftIndex;
	const safeRightIndex = rightIndex === -1 ? order.length : rightIndex;

	if (safeLeftIndex < safeRightIndex) {
		return -1;
	}

	if (safeLeftIndex > safeRightIndex) {
		return 1;
	}

	return 0;
}

function compareRowFieldValues(leftValue, rightValue, field) {
	if (field === "ctitle") {
		return compareCtitles(leftValue, rightValue);
	}

	if (field === "evaluate") {
		return compareEvaluateValues(leftValue, rightValue);
	}

	if (leftValue === rightValue) {
		return 0;
	}

	if (leftValue === null || leftValue === undefined || leftValue === "") {
		return 1;
	}

	if (rightValue === null || rightValue === undefined || rightValue === "") {
		return -1;
	}

	if (typeof leftValue === "number" && typeof rightValue === "number") {
		return leftValue - rightValue;
	}

	if (!Number.isNaN(Number(leftValue)) && !Number.isNaN(Number(rightValue))) {
		return Number(leftValue) - Number(rightValue);
	}

	return String(leftValue).localeCompare(String(rightValue), undefined, {
		numeric: true,
		sensitivity: "base",
	});
}

function getColumnAlignmentClasses(alignment = "left") {
	if (alignment === "center") {
		return "text-center";
	}

	if (alignment === "right") {
		return "text-right";
	}

	return "text-left";
}

function getColumnMenuFieldOptions(column) {
	return Array.isArray(column?.menuFieldOptions) ? column.menuFieldOptions : [];
}

function columnUsesMenuFieldOptions(column) {
	return getColumnMenuFieldOptions(column).length > 0;
}

function isColumnSortActive(column, sortState) {
	if (sortState.field === column.field) {
		return true;
	}

	return getColumnMenuFieldOptions(column).some((option) => option.field === sortState.field);
}

function getColumnHeaderFilterValue(column, activeColumnFilters) {
	const menuFieldOptions = getColumnMenuFieldOptions(column);

	if (menuFieldOptions.length === 0) {
		return activeColumnFilters[column.field] ?? "";
	}

	return menuFieldOptions
		.map((option) => activeColumnFilters[option.field])
		.filter((value) => normalizeFilterValue(value))
		.join(", ");
}

function getColumnMenuFieldLabel(column, field) {
	const matchedOption = getColumnMenuFieldOptions(column).find((option) => option.field === field);
	return matchedOption?.label ?? column?.headerName ?? "";
}

function getThemeRowColor(rowClassName, theme) {
	if (rowClassName.includes("super-app-theme--noEvaluation")) {
		return theme.palette.george?.main ?? "#f74367";
	}

	if (rowClassName.includes("super-app-theme--evaluation")) {
		return theme.palette.success?.main ?? "#275d38";
	}

	if (rowClassName.includes("super-app-theme--pending")) {
		return theme.palette.pending?.main ?? "#f5c242";
	}

	if (rowClassName.includes("super-app-theme--J")) {
		return theme.palette.info?.main ?? "#0288d1";
	}

	return null;
}

function getRowPresentation({ rowClassName, theme, isSelected, isClickable }) {
	const themedColor = getThemeRowColor(rowClassName, theme);
	const backgroundColor = themedColor
		? alpha(themedColor, isSelected ? 0.22 : 0.12)
		: isSelected
			? alpha(theme.palette.secondaryLime.light, 0.1)
			: undefined;
	const boxShadow = [];

	if (rowClassName.includes("spot-group-child-combined")) {
		boxShadow.push("inset 4px 0 0 #1d4ed8");
	} else if (rowClassName.includes("spot-group-child")) {
		boxShadow.push("inset 4px 0 0 #275d38");
	}

	if (isSelected) {
		boxShadow.push(`inset 0 0 0 1px ${alpha(theme.palette.secondaryLime.light, 0.22)}`);
	}

	return {
		className: [
			"transition-colors ",
			isClickable ? "cursor-pointer" : "cursor-default",
			backgroundColor ? "hover:brightness-[0.985]" : isClickable ? "hover:bg-gray-50" : "",
		]
			.filter(Boolean)
			.join(" "),
		style: {
			backgroundColor,
			boxShadow: boxShadow.length > 0 ? boxShadow.join(", ") : undefined,
		},
	};
}

function sortGroupedRows(rows, sortState, columnsByField) {
	if (!Array.isArray(rows) || rows.length === 0) {
		return [];
	}

	const groupsByRootId = new Map();
	const orderedRootIds = [];

	rows.forEach((row) => {
		const rootId = row._groupRootId ?? row.classid;
		if (!groupsByRootId.has(rootId)) {
			groupsByRootId.set(rootId, []);
			orderedRootIds.push(rootId);
		}

		groupsByRootId.get(rootId).push(row);
	});

	orderedRootIds.forEach((rootId) => {
		groupsByRootId
			.get(rootId)
			.sort((leftRow, rightRow) => (leftRow._groupOrder ?? 0) - (rightRow._groupOrder ?? 0));
	});

	const sortedRootIds = [...orderedRootIds].sort((leftRootId, rightRootId) => {
		const leftRootRow = groupsByRootId.get(leftRootId)?.[0];
		const rightRootRow = groupsByRootId.get(rightRootId)?.[0];
		const sortColumn = columnsByField.get(sortState.field);

		if (!leftRootRow || !rightRootRow) {
			return 0;
		}

		const comparison = compareRowFieldValues(
			getColumnSortValue(leftRootRow, sortColumn),
			getColumnSortValue(rightRootRow, sortColumn),
			sortState.field,
		);

		if (comparison === 0) {
			return 0;
		}

		return sortState.direction === "desc" ? -comparison : comparison;
	});

	return sortedRootIds.flatMap((rootId) => groupsByRootId.get(rootId) ?? []);
}

function ColumnHeaderButton({ column, sortState, filterValue, onToggleSort, onOpenMenu, headerIcon, isConstrainedLayout }) {
	const isActive = isColumnSortActive(column, sortState);
	const menuActive = isActive || Boolean(filterValue);
	const resolvedHeaderIcon = React.isValidElement(headerIcon)
		? headerIcon
		: headerIcon
			? React.createElement(headerIcon, { fontSize: "small" })
			: null;
	const sortArrow = isActive ? (
		sortState.direction === "asc" ? (
			<ArrowUpwardIcon fontSize="small" />
		) : (
			<ArrowDownwardIcon fontSize="small" />
		)
	) : (
		<SwapVertIcon fontSize="small" />
	);
	const canOpenMenu = column.sortable || column.filterable;
	const justifyClass =
		column.headerAlign === "center"
			? "justify-center"
			: column.headerAlign === "right"
				? "justify-end"
				: "justify-start";

	return (
		<div className={`flex min-w-0 items-center gap-0.5 ${justifyClass}`}>
			{column.sortable ? (
				<button
					type="button"
					className={`min-w-0 truncate text-xs font-semibold ${menuActive ? "text-primary" : "text-black"}`}
					onClick={(event) => {
						if (isConstrainedLayout && canOpenMenu) {
							onOpenMenu(column.field, event);
							return;
						}

						if (columnUsesMenuFieldOptions(column)) {
							onOpenMenu(
								column.field,
								event,
								isActive && sortState.direction === "asc" ? "sortDesc" : "sortAsc",
							);
							return;
						}

						onToggleSort(column.field);
					}}
				>
					{isConstrainedLayout && resolvedHeaderIcon ? <span className={`${menuActive ? "text-primary" : "text-black"} px-0 `}>{resolvedHeaderIcon}</span> : (<>{column.headerName}<span className={`${menuActive ? "text-primary" : "text-black"} px-0 ml-0.5`}>{resolvedHeaderIcon}</span></>)}
				</button>
			) : (
				<span className="min-w-0 truncate text-xs font-semibold text-black">
					{isConstrainedLayout && resolvedHeaderIcon ? <span className={`${menuActive ? "text-primary" : "text-black"} px-0 `}>{resolvedHeaderIcon}</span> : (<>{column.headerName}<span className={`${menuActive ? "text-primary" : "text-black"} px-0 ml-0.5`}>{resolvedHeaderIcon}</span></>)}
				</span>
			)}
			
			{canOpenMenu && !isConstrainedLayout ? (
				<button
					type="button"
					className={`relative inline-flex shrink-0 items-center rounded p-0.5 transition-colors ${
						menuActive ? "text-primary" : "text-gray-500 hover:text-black"
					}`}
					onClick={(event) => onOpenMenu(column.field, event)}
					aria-label={`Open sort and filter menu for ${column.headerName}`}
				>
					{sortArrow}
					{filterValue ? (
						<span className="absolute right-0 top-0 h-2 w-2 rounded-full bg-orange-500" />
					) : null}
				</button>
			) : null}
		</div>
	);
}

function DateFieldTargetMenu({ column, action, activeColumnFilters, onBack, onChooseField }) {
	const actionLabel =
		action === "sortAsc"
			? "Sort ASC by"
			: action === "sortDesc"
				? "Sort DESC by"
				: "Filter by";

	return (
		<div className="min-w-[160px] py-1">
			<p className="px-4 pb-1 pt-1 text-[11px] font-semibold uppercase text-gray-500">
				{actionLabel}
			</p>
			{getColumnMenuFieldOptions(column).map((option) => {
				const activeFilterValue = activeColumnFilters[option.field];

				return (
					<MenuItem key={option.field} onClick={() => onChooseField(option.field)}>
						<div className="flex w-full items-center justify-between gap-3">
							<span>{option.label}</span>
							{activeFilterValue ? (
								<span className="h-2 w-2 rounded-full bg-orange-500" />
							) : null}
						</div>
					</MenuItem>
				);
			})}
			<MenuItem onClick={onBack}>
				<span className="text-xs font-semibold text-gray-600">Back</span>
			</MenuItem>
		</div>
	);
}

function TablePaginationControls({
	countEvaluateYes,
	countEvaluateNo,
	countEvaluateUndefined,
	currentPage,
	pageCount,
	rowCount,
	pageStart,
	pageEnd,
	onPrevious,
	onNext,
}) {
	return (
		<div className="flex items-center justify-between gap-3 border-t border-gray-300 bg-white px-4 py-3">
			<div className="text-xs w-1/2 flex gap-2">
				<div className="flex gap-2 items-center justify-between shadow-inner border rounded bg-green-50">
					<p className="p-1 text-center">Submitted for SPOT</p>
					<p className="text-sm font-semibold text-gray-900 border-l h-full flex flex-col items-center justify-center px-2 bg-white rounded-r">
						{countEvaluateYes}
					</p>
				</div>
				<div className="flex gap-2 items-center justify-between shadow-inner border rounded bg-red-50">
					<p className="p-1 text-center">Excluded </p>
					<p className="text-sm font-semibold text-gray-900 border-l h-full flex flex-col items-center justify-center px-2 bg-white rounded-r">
						{countEvaluateNo}
					</p>
				</div>

				<div className="flex gap-2 items-center justify-between shadow-inner border rounded">
					<p className="p-1 text-center">Pending</p>
					<p className="text-sm font-semibold text-gray-900 border-l h-full flex flex-col items-center justify-center px-2 bg-white rounded-r">
						{countEvaluateUndefined}
					</p>
				</div>
			</div>
			<div className="flex gap-12 items-center">
				<p className="text-xs font-medium text-gray-600">
					{rowCount === 0 ? "No classes" : `Showing ${pageStart}-${pageEnd} of ${rowCount}`}
				</p>
				<div className="flex items-center gap-2">
					<button
						type="button"
						className="rounded border border-gray-300 px-3 py-1.5 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
						onClick={onPrevious}
						disabled={currentPage === 0 || pageCount === 0}
					>
						Previous
					</button>
					<p className="min-w-[96px] text-center text-xs font-semibold text-gray-700">
						Page {pageCount === 0 ? 0 : currentPage + 1} of {pageCount}
					</p>
					<button
						type="button"
						className="rounded border border-gray-300 px-3 py-1.5 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
						onClick={onNext}
						disabled={pageCount === 0 || currentPage >= pageCount - 1}
					>
						Next
					</button>
				</div>
			</div>
		</div>
	);
}

const DataGridTable = ({
	rows,
	loading,
	getRowClassName,
	filterKey,
	setOpenSelectedClassModal,
	reviewingClasses,
	selectedBulkClassIds,
	confirmSubmitAllSelected,
	renderInstructorNameTable,
	validateClass,
	dateFormat,
	fetchedClassIdsLoading,
	handleOpenAddInstancedClass,
	hasExternalFiltersApplied,
	onClearExternalFilters,
}) => {
	const theme = useTheme();
	const setSelectedClass = useSetAtom(selectedClassAtom);
	const unsavedChanges = useAtomValue(unsavedChangesAtom);
	const [selectionModel, setSelectionModel] = useAtom(spotOrderingSelectionModelAtom);
	const [sortState, setSortState] = useState({ field: "ctitle", direction: "asc" });
	const [currentPage, setCurrentPage] = useState(0);
	const [columnVisibilityModel, setColumnVisibilityModel] = useState(
		DEFAULT_COLUMN_VISIBILITY_MODEL,
	);
	const [columnFilters, setColumnFilters] = useState({});
	const [headerMenu, setHeaderMenu] = useState(null);
	const [rowActionMenu, setRowActionMenu] = useState(null);
	const filterInputRef = useRef(null);

	const hasBulkSelection = selectedBulkClassIds.length > 0;
	const selectionDisabled =
		unsavedChanges || reviewingClasses || confirmSubmitAllSelected || fetchedClassIdsLoading;
	const singleClassEditingDisabled =
		unsavedChanges ||
		fetchedClassIdsLoading ||
		hasBulkSelection ||
		reviewingClasses ||
		confirmSubmitAllSelected;
	const isConstrainedLayout = hasBulkSelection || reviewingClasses || confirmSubmitAllSelected;
	const shouldStackDateColumns = true;
	const selectionColumnWidth = isConstrainedLayout ? 38 : 52;
	const classIdColumnWidth = isConstrainedLayout ? 54 : 60;
	const classSizeColumnWidth = isConstrainedLayout ? 42 : 40;
	const compactColumnWidth = isConstrainedLayout ? 58 : 92;
	const compactDateColumnWidth = isConstrainedLayout ? 84 : 80;
	const compactDatePairColumnWidth = isConstrainedLayout ? 92 : 112;
	const narrowColumnWidth = isConstrainedLayout ? 56 : 64;
	const iconColumnWidth = isConstrainedLayout ? 52 : 68;
	const manageColumnWidth = isConstrainedLayout ? 64 : 80;
	const tableDateFormat = isConstrainedLayout ? "DD/MM/YY" : dateFormat;
	const tableDisabled = unsavedChanges || fetchedClassIdsLoading;

	useEffect(() => {
		if (hasBulkSelection || reviewingClasses || confirmSubmitAllSelected) {
			setColumnVisibilityModel((prev) => ({
				...prev,
				form: true,
				survey_start: true,
				survey_end: true,
				evaluate: false,
				manageAction: false,
			}));
		} else {
			setColumnVisibilityModel((prev) => ({
				...prev,
				form: true,
				survey_start: true,
				survey_end: true,
				evaluate: true,
				manageAction: true,
			}));
		}
	}, [confirmSubmitAllSelected, hasBulkSelection, reviewingClasses]);

	useEffect(() => {
		if (reviewingClasses) {
			setSortState({ field: "evaluate", direction: "asc" });
			return;
		}

		setSortState({ field: "ctitle", direction: "asc" });
	}, [reviewingClasses]);

	useEffect(() => {
		setCurrentPage(0);
		setHeaderMenu(null);
		setRowActionMenu(null);
	}, [filterKey]);

	useEffect(() => {
		if (
			rowActionMenu &&
			(selectionDisabled || hasBulkSelection || reviewingClasses || confirmSubmitAllSelected)
		) {
			setRowActionMenu(null);
		}
	}, [
		confirmSubmitAllSelected,
		hasBulkSelection,
		reviewingClasses,
		rowActionMenu,
		selectionDisabled,
	]);

	useEffect(() => {
		if (!headerMenu?.isFilterEditorOpen) {
			return undefined;
		}

		const frameId = window.requestAnimationFrame(() => {
			filterInputRef.current?.focus();
			filterInputRef.current?.select?.();
		});

		return () => window.cancelAnimationFrame(frameId);
	}, [headerMenu?.isFilterEditorOpen]);

	const isRowSelectable = useCallback(
		(row) =>
			row?.csize >= 4 &&
			row?.evaluate !== "Yes" &&
			row?.evaluate !== "No" &&
			!unsavedChanges &&
			!reviewingClasses &&
			!confirmSubmitAllSelected &&
			!fetchedClassIdsLoading,
		[confirmSubmitAllSelected, fetchedClassIdsLoading, reviewingClasses, unsavedChanges],
	);

	const composedRowClassName = useCallback(
		(row) => {
			const classes = [];

			if (typeof getRowClassName === "function") {
				const baseClassName = getRowClassName({ row });
				if (baseClassName) {
					classes.push(baseClassName);
				}
			}

			if (row._isGroupRoot && row._hasGroupedChildren) {
				classes.push("spot-group-root");
			}

			if (row._isGroupedChild) {
				classes.push("spot-group-child");
				if (row._groupChildType === "combined") {
					classes.push("spot-group-child-combined");
				}
			}

			return classes.join(" ");
		},
		[getRowClassName],
	);

	const handleOpenSelectedClassModal = useCallback(
		(row) => {
			if (singleClassEditingDisabled) {
				return;
			}

			setSelectedClass(row);
			setOpenSelectedClassModal(true);
		},
		[setOpenSelectedClassModal, setSelectedClass, singleClassEditingDisabled],
	);

	const handleOpenAddInstancedClassFunction = useCallback(
		(row) => {
			if (singleClassEditingDisabled) {
				return;
			}
			setSelectedClass(row);
			handleOpenAddInstancedClass();
		},
		[handleOpenAddInstancedClass, setSelectedClass, singleClassEditingDisabled],
	);

	const openRowActionMenu = useCallback((row, event) => {
		setRowActionMenu({
			row,
			mouseX: event.clientX + 2,
			mouseY: event.clientY - 6,
		});
	}, []);

	const handleCloseRowActionMenu = useCallback(() => {
		setRowActionMenu(null);
	}, []);

	const commitColumnFilter = useCallback((field, value) => {
		const normalizedValue = String(value ?? "").trim();

		setColumnFilters((prev) => {
			const nextFilters = { ...prev };

			if (normalizedValue) {
				nextFilters[field] = normalizedValue;
			} else {
				delete nextFilters[field];
			}

			return nextFilters;
		});
		setCurrentPage(0);
	}, []);

	const handleOpenHeaderMenu = useCallback(
		(field, event, pendingAction = null) => {
			event.stopPropagation();

			setHeaderMenu({
				field,
				anchorEl: event.currentTarget,
				isFilterEditorOpen: false,
				pendingAction,
				targetField: null,
				draftFilterValue: columnFilters[field] ?? "",
			});
		},
		[columnFilters],
	);

	const handleCloseHeaderMenu = useCallback(() => {
		const filterField = headerMenu?.targetField ?? headerMenu?.field;

		if (headerMenu?.isFilterEditorOpen && filterField) {
			commitColumnFilter(filterField, headerMenu.draftFilterValue);
		}

		setHeaderMenu(null);
	}, [commitColumnFilter, headerMenu]);

	const handleOpenColumnFilterEditor = useCallback(() => {
		setHeaderMenu((prev) =>
			prev
				? {
						...prev,
						targetField: prev.targetField ?? prev.field,
						pendingAction: null,
						isFilterEditorOpen: true,
					}
				: prev,
		);
	}, []);

	const handleStartHeaderMenuAction = useCallback((action) => {
		setHeaderMenu((prev) =>
			prev
				? {
						...prev,
						pendingAction: action,
						isFilterEditorOpen: false,
					}
				: prev,
		);
	}, []);

	const handleBackToHeaderMenuActions = useCallback(() => {
		setHeaderMenu((prev) =>
			prev
				? {
						...prev,
						pendingAction: null,
					}
				: prev,
		);
	}, []);

	const handleHeaderMenuDraftChange = useCallback((value) => {
		setHeaderMenu((prev) =>
			prev
				? {
						...prev,
						draftFilterValue: value,
					}
				: prev,
		);
	}, []);

	const handleSetSort = useCallback((field, direction) => {
		if (!field) {
			return;
		}

		setSortState({ field, direction });
		setHeaderMenu(null);
	}, []);

	const handleChooseHeaderMenuField = useCallback(
		(field) => {
			if (!field) {
				return;
			}

			const pendingAction = headerMenu?.pendingAction;

			if (pendingAction === "sortAsc" || pendingAction === "sortDesc") {
				handleSetSort(field, pendingAction === "sortAsc" ? "asc" : "desc");
				return;
			}

			if (pendingAction === "filter") {
				setHeaderMenu((prev) =>
					prev
						? {
								...prev,
								targetField: field,
								pendingAction: null,
								isFilterEditorOpen: true,
								draftFilterValue: columnFilters[field] ?? "",
							}
						: prev,
				);
			}
		},
		[columnFilters, handleSetSort, headerMenu?.pendingAction],
	);

	const handleClearAllFilters = useCallback(() => {
		setColumnFilters({});
		setCurrentPage(0);
		setHeaderMenu(null);
		if (typeof onClearExternalFilters === "function") {
			onClearExternalFilters();
		}
	}, [onClearExternalFilters]);

	const handleClearHeaderColumnFilters = useCallback((column) => {
		if (!column) {
			return;
		}

		const fieldsToClear = columnUsesMenuFieldOptions(column)
			? getColumnMenuFieldOptions(column).map((option) => option.field)
			: [column.field];

		setColumnFilters((prev) => {
			const nextFilters = { ...prev };
			fieldsToClear.forEach((field) => {
				delete nextFilters[field];
			});
			return nextFilters;
		});
		setCurrentPage(0);
		setHeaderMenu(null);
	}, []);

	const selectedIds = useMemo(
		() => getSelectedIdsFromModel(selectionModel, rows),
		[rows, selectionModel],
	);
	const selectedIdsSet = useMemo(() => new Set(selectedIds), [selectedIds]);

	const toggleSelectedRow = useCallback(
		(rowId, shouldSelect) => {
			const nextSelectedIds = new Set(getSelectedIdsFromModel(selectionModel, rows));
			const rowAlreadySelected = nextSelectedIds.has(rowId);
			const nextChecked = shouldSelect ?? !rowAlreadySelected;

			if (nextChecked) {
				nextSelectedIds.add(rowId);
			} else {
				nextSelectedIds.delete(rowId);
			}

			setSelectionModel({
				type: "include",
				ids: nextSelectedIds,
			});
		},
		[rows, selectionModel, setSelectionModel],
	);

	const handleAddRowToBulkSelection = useCallback(
		(row) => {
			if (!row || !isRowSelectable(row)) {
				return;
			}

			toggleSelectedRow(row.classid, true);
			setRowActionMenu(null);
		},
		[isRowSelectable, toggleSelectedRow],
	);

	const handleRowClick = useCallback(
		(row, event) => {
			const rowIsSelectable = isRowSelectable(row);

			if (!hasBulkSelection) {
				if (selectionDisabled && !rowIsSelectable) {
					return;
				}

				openRowActionMenu(row, event);
				return;
			}

			if (selectionDisabled || !rowIsSelectable) {
				return;
			}

			toggleSelectedRow(row.classid);
		},
		[hasBulkSelection, isRowSelectable, openRowActionMenu, selectionDisabled, toggleSelectedRow],
	);

	const handleRowContextMenu = useCallback(
		(row, event) => {
			if (hasBulkSelection) {
				return;
			}

			const rowIsSelectable = isRowSelectable(row);
			if (singleClassEditingDisabled && !rowIsSelectable) {
				return;
			}

			event.preventDefault();
			openRowActionMenu(row, event);
		},
		[hasBulkSelection, isRowSelectable, openRowActionMenu, singleClassEditingDisabled],
	);

	const renderGroupedClassIdCell = useCallback((row) => {
		const depth = row._groupDepth ?? 0;
		const indent = Math.min(depth, 4) * 12;

		return (
			<div className="flex h-full w-full items-center gap-2 overflow-hidden">
				{depth > 0 && (
					<span className="text-gray-400" style={{ paddingLeft: `${indent}px` }}>
						<SubdirectoryArrowRightIcon fontSize="small" />
					</span>
				)}
				<p
					className={`truncate ${isConstrainedLayout ? "text-[8pt]" : "text-md"} ${
						row._isGroupedChild
							? " font-medium text-gray-700"
							: "  text-black"
					}`}
				>
					{row.classid}
				</p>
			</div>
		);
	}, []);

	const renderGroupedClassTitleCell = useCallback((row) => {
		const depth = row._groupDepth ?? 0;
		const indent = Math.min(depth, 4) * 12;

		return (
			<div className="flex h-full w-full items-center justify-center overflow-hidden">
				<div
					className="flex w-full items-center gap-2 overflow-hidden"
					style={{ paddingLeft: `${indent}px` }}
				>
					<p
						className={`truncate  ${row.evaluate === "No" ? "line-through text-gray-700" : ""} ${
							row._isGroupedChild ? "font-medium text-gray-700" : "font-semibold text-black"
						} ${isConstrainedLayout ? "text-[8pt]" : "text-xs"}`}
					>
						{row.ctitle}
					</p>
				</div>
			</div>
		);
	}, []);

	const renderTAOrPI = useCallback((row) => {
		if (row?.roles?.length > 0) {
			if (row.roles.every((role) => role.class_role !== "TA")) {
				return (
					<Tooltip
						title={`Roles: ${row.roles.map((role) => role.class_role).join(", ")}`}
						placement="right"
					>
						<p className={`flex h-full  justify-start text-xs font-semibold text-green-800 ${isConstrainedLayout ? "text-[8pt]" : "text-md"}`}>
							~PI
						</p>
					</Tooltip>
				);
			}

			if (row.roles.every((role) => role.class_role === "TA")) {
				return (
					<Tooltip
						title={`Roles: ${row.roles.map((role) => role.class_role).join(", ")}`}
						placement="right"
					>
						<p className={`flex h-full items-center justify-start text-xs font-semibold text-red-800 ${isConstrainedLayout ? "text-[8pt]" : "text-md"}`}>
							TA
						</p>
					</Tooltip>
				);
			}

			return (
				<Tooltip
					title={`Roles: ${row.roles.map((role) => role.class_role).join(", ")}`}
					placement="right"
				>
					<p className={`flex h-full items-center justify-start text-xs font-semibold text-orange-500 ${isConstrainedLayout ? "text-[8pt]" : "text-md"}`}>
						Mix
					</p>
				</Tooltip>
			);
		}

		return (
			null
		);
	}, []);

	const renderEvaluateIndicator = useCallback(
		(row) => {
			if (row.evaluate === "Yes") {
				return (
					<Tooltip title="Submitted in BlueCourses" placement="right">
						<CheckCircleIcon color="primary" />
					</Tooltip>
				);
			}

			if (row.evaluate === "Pending") {
				return (
					<Tooltip title="Pending bulk submission review" placement="right">
						<Checkbox
							checked
							disabled
							sx={{ color: "#FFB600", "&.Mui-checked": { color: "#FFB600" } }}
						/>
					</Tooltip>
				);
			}

			if (row.evaluate === "No") {
				return (
					<Tooltip title="Excluded from submission" placement="right">
						<DoNotDisturbIcon color="error" />
					</Tooltip>
				);
			}

			const validationState = validateClass(row);
			if (validationState.shouldPrevent) {
				return (
					<Tooltip placement="right" title={validationState.message}>
						<WarningIcon color="warning" />
					</Tooltip>
				);
			}

			return null;
		},
		[validateClass],
	);

	const renderStackedDates = useCallback(
		(dateItems) => (
			<div className="flex h-full min-w-0 flex-col justify-center gap-0.5 text-left leading-tight">
				{dateItems.map(({ key, label, value }) => (
					<p key={key} className={`flex min-w-0 items-center justify-between border-b gap-1  font-semibold text-black ${isConstrainedLayout ? "text-[8pt]" : "text-md"}`}>
						<span className="shrink-0 text-[9px] font-bold uppercase text-gray-500">{label}</span>
						<span className="truncate">{value ? dayjs(value).format(tableDateFormat) : ""}</span>
					</p>
				))}
			</div>
		),
		[tableDateFormat],
	);

	const columns = useMemo(
		() => [
			{
				field: "__select",
				headerName: "Select",
				align: "center",
				headerAlign: "center",
				sortable: false,
				filterable: false,
				width: selectionColumnWidth,
				minWidth: selectionColumnWidth,
				isVisible: !reviewingClasses,
				renderCell: (row) => {
					const rowIsSelectable = isRowSelectable(row);

					if (!rowIsSelectable) {
						return <div className="mx-auto h-6 w-6" />;
					}

					return (
						<div
							className="flex items-center justify-center"
							onClick={(event) => event.stopPropagation()}
						>
							<Checkbox
								size="small"
								disableRipple
								sx={{ p: 0.25 }}
								checked={selectedIdsSet.has(row.classid)}
								onChange={(event) => toggleSelectedRow(row.classid, event.target.checked)}
							/>
						</div>
					);
				},
			},{
				field: "evaluate",
				headerName: "",
				align: "center",
				headerAlign: "center",
				sortable: true,
				filterable: true,
				width: 20,
				minWidth: 20,
				getSortValue: (row) => {
					if (row.evaluate === "Yes" || row.evaluate === "Pending" || row.evaluate === "No") {
						return row.evaluate;
					}

					const validationState = validateClass(row);
					return validationState.shouldPrevent ? "Caution" : "";
				},
				getFilterValue: (row) => {
					if (row.evaluate === "Yes" || row.evaluate === "Pending" || row.evaluate === "No") {
						return row.evaluate;
					}

					const validationState = validateClass(row);
					return validationState.shouldPrevent ? "Caution" : "";
				},
				renderCell: renderEvaluateIndicator,
			},
			{
				field: "classid",
				headerName: "ID",
				align: "left",
				headerAlign: "left",
				sortable: true,
				filterable: true,
				width: classIdColumnWidth,
				minWidth: classIdColumnWidth,
				getSortValue: (row) => row.classid,
				getFilterValue: (row) => row.classid,
				renderCell: renderGroupedClassIdCell,
			},
			{
				field: "ctitle",
				headerName: "Class Title",
				align: "center",
				headerAlign: "center",
				sortable: true,
				filterable: true,
				minWidth: 100,
				width: isConstrainedLayout ? 80 : 120,
				getSortValue: (row) => row.ctitle,
				getFilterValue: (row) => row.ctitle,
				renderCell: renderGroupedClassTitleCell,
			},
			{
				field: "csize",
				headerName: "STD",
				align: "center",
				headerAlign: "center",
				sortable: true,
				filterable: true,
				width: classSizeColumnWidth,
				minWidth: classSizeColumnWidth,
				headerIcon: SchoolIcon,
				getSortValue: (row) => row.csize,
				getFilterValue: (row) => row.csize,
				renderCell: (row) => (
					<div className="flex h-full items-center justify-center">
						<p className={`  text-black ${isConstrainedLayout ? "text-[8pt]" : "text-md"}`}>{row.csize}</p>
					</div>
				),
			},
			{
				field: "form",
				headerName: "Block",
				align: "center",
				headerAlign: "center",
				sortable: true,
				filterable: true,
				width: compactColumnWidth,
				minWidth: compactColumnWidth,
				headerIcon: FormatListNumberedIcon,
				getSortValue: (row) => row.form,
				getFilterValue: (row) => row.form,
				renderCell: (row) => (
					<p className={` flex h-full items-center justify-center   text-black ${isConstrainedLayout ? "text-[8pt]" : "text-md"}`}>
						{row.form ? row.form : ""}
					</p>
				),
			},
			{
				field: "name",
				headerName: "Instructors",
				align: "center",
				//headerAlign: "center",
				sortable: true,
				filterable: true,
				minWidth: 100,
				headerIcon: RecordVoiceOverIcon,
				width: isConstrainedLayout ? 100 : 150,
				getSortValue: (row) => getInstructorNamesText(row),
				getFilterValue: (row) => getInstructorNamesText(row),
				renderCell: (row) => renderInstructorNameTable({ row }, isConstrainedLayout),
			},
			{
				field: "taOrPI",
				headerName: "Roles",
				//align: "center",
				//headerAlign: "center",
				sortable: true,
				filterable: true,
				width: 40,
				headerIcon: BadgeIcon,
				minWidth: 40,
				getSortValue: (row) => getRoleFilterText(row),
				getFilterValue: (row) => getRoleFilterText(row),
				renderCell: renderTAOrPI,
			},
			{
				field: "c_location",
				headerName: "Loc",
				align: "center",
				headerAlign: "center",
				sortable: true,
				filterable: true,
				width: compactColumnWidth,
				minWidth: compactColumnWidth,
				getSortValue: (row) => row.c_location,
				getFilterValue: (row) => row.c_location,
				renderCell: (row) => (
					<p className="text-xs font-semibold text-black">{row.c_location ? row.c_location : ""}</p>
				),
			},
			{
				field: "course_dates",
				headerName: "Course",
				align: "left",
				headerAlign: "left",
				sortable: true,
				filterable: true,
				headerIcon: CalendarMonthIcon,
				width: 70,
				minWidth: 70,
				isVisible: shouldStackDateColumns,
				menuFieldOptions: [
					{ field: "c_edate", label: "C_EDATE" },
					{ field: "examdate", label: "EXAMDATE" },
				],
				getSortValue: (row) => row.c_edate,
				getFilterValue: (row) =>
					[row.c_edate, row.examdate]
						.filter(Boolean)
						.map((value) => dayjs(value).format(tableDateFormat))
						.join(" "),
				renderCell: (row) =>
					renderStackedDates([
						{ key: "course-end", label: "End", value: row.c_edate },
						{ key: "exam", label: "Exam", value: row.examdate },
					]),
			},
			{
				field: "c_edate",
				headerName: "End Date",
				align: "center",
				headerAlign: "center",
				sortable: true,
				filterable: true,
				width: compactDateColumnWidth,
				minWidth: compactDateColumnWidth,
				isVisible: !shouldStackDateColumns,
				getSortValue: (row) => row.c_edate,
				getFilterValue: (row) => (row.c_edate ? dayjs(row.c_edate).format(tableDateFormat) : ""),
				renderCell: (row) => (
					<div className="flex h-full items-center justify-center">
						<p className="text-xs font-semibold text-black">
							{row.c_edate && dayjs(row.c_edate).format(tableDateFormat)}
						</p>
					</div>
				),
			},
			{
				field: "examdate",
				headerName: "Exam Date",
				align: "center",
				headerAlign: "center",
				sortable: true,
				filterable: true,
				width: compactDateColumnWidth,
				minWidth: compactDateColumnWidth,
				isVisible: !shouldStackDateColumns,
				getSortValue: (row) => row.examdate,
				getFilterValue: (row) => (row.examdate ? dayjs(row.examdate).format(tableDateFormat) : ""),
				renderCell: (row) => (
					<div className="flex h-full items-center justify-center">
						<p className="text-xs font-semibold text-black">
							{row.examdate && dayjs(row.examdate).format(tableDateFormat)}
						</p>
					</div>
				),
			},
			{
				field: "survey_window",
				headerName: "Survey",
				align: "left",
				headerAlign: "left",
				sortable: true,
				filterable: true,
				width: 70,
				minWidth: 70,
				headerIcon: EventIcon,
				isVisible: shouldStackDateColumns,
				menuFieldOptions: [
					{ field: "survey_start", label: "SURVEY_START" },
					{ field: "survey_end", label: "SURVEY_END" },
				],
				getSortValue: (row) => row.survey_start,
				getFilterValue: (row) =>
					[row.survey_start, row.survey_end]
						.filter(Boolean)
						.map((value) => dayjs(value).format(tableDateFormat))
						.join(" "),
				renderCell: (row) =>
					renderStackedDates([
						{ key: "survey-start", label: "Start", value: row.survey_start },
						{ key: "survey-end", label: "End", value: row.survey_end },
					]),
			},
			{
				field: "survey_start",
				headerName: "Survey Start",
				align: "center",
				headerAlign: "center",
				sortable: true,
				filterable: true,
				width: compactDateColumnWidth + 4,
				minWidth: compactDateColumnWidth + 4,
				isVisible: !shouldStackDateColumns,
				getSortValue: (row) => row.survey_start,
				getFilterValue: (row) =>
					row.survey_start ? dayjs(row.survey_start).format(tableDateFormat) : "",
				renderCell: (row) => (
					<div className="flex h-full items-center justify-center">
						<p className="text-xs font-semibold text-black">
							{row.survey_start && dayjs(row.survey_start).format(tableDateFormat)}
						</p>
					</div>
				),
			},
			{
				field: "survey_end",
				headerName: "Survey End",
				align: "center",
				headerAlign: "center",
				sortable: true,
				filterable: true,
				width: compactDateColumnWidth,
				minWidth: compactDateColumnWidth,
				isVisible: !shouldStackDateColumns,
				getSortValue: (row) => row.survey_end,
				getFilterValue: (row) =>
					row.survey_end ? dayjs(row.survey_end).format(tableDateFormat) : "",
				renderCell: (row) => (
					<div className="flex h-full items-center justify-center">
						<p className="text-xs font-semibold text-black">
							{row.survey_end && dayjs(row.survey_end).format(tableDateFormat)}
						</p>
					</div>
				),
			},
			
			{
				field: "manageAction",
				headerName: "Manage",
				align: "center",
				headerAlign: "center",
				sortable: false,
				filterable: false,
				width: 40,
				minWidth: 40,
				renderCell: (row) => (
					<button
						type="button"
						className="mt-[1px] flex h-8 w-full flex-col items-center justify-center rounded bg-primary/80 text-white hover:bg-primary disabled:cursor-not-allowed disabled:bg-gray-300"
						onClick={(event) => {
							event.stopPropagation();
							handleOpenSelectedClassModal(row);
						}}
						disabled={singleClassEditingDisabled}
					>
						<BuildCircleIcon fontSize="inherit" sx={{ width: 25, height: 25, color: "#ffffff" }} />
					</button>
				),
			},
		],
		[
			compactColumnWidth,
			compactDateColumnWidth,
			classIdColumnWidth,
			classSizeColumnWidth,
			handleOpenSelectedClassModal,
			isConstrainedLayout,
			isRowSelectable,
			renderEvaluateIndicator,
			renderGroupedClassIdCell,
			renderGroupedClassTitleCell,
			renderInstructorNameTable,
			renderStackedDates,
			renderTAOrPI,
			reviewingClasses,
			selectionColumnWidth,
			selectedIdsSet,
			shouldStackDateColumns,
			singleClassEditingDisabled,
			tableDateFormat,
			toggleSelectedRow,
			validateClass,

		],
	);

	const columnsByField = useMemo(
		() => new Map(columns.map((column) => [column.field, column])),
		[columns],
	);
	const visibleColumns = useMemo(
		() =>
			columns.filter(
				(column) => (column.isVisible ?? true) && columnVisibilityModel[column.field] !== false,
			),
		[columnVisibilityModel, columns],
	);
	const activeColumnFilters = useMemo(() => {
		const visibleFilterableFields = new Set();
		visibleColumns
			.filter((column) => column.filterable !== false)
			.forEach((column) => {
				visibleFilterableFields.add(column.field);
				getColumnMenuFieldOptions(column).forEach((option) => {
					visibleFilterableFields.add(option.field);
				});
			});

		return Object.fromEntries(
			Object.entries(columnFilters).filter(
				([field, value]) => visibleFilterableFields.has(field) && normalizeFilterValue(value),
			),
		);
	}, [columnFilters, visibleColumns]);
	const filteredRows = useMemo(
		() => filterGroupedRows(rows, activeColumnFilters, columnsByField),
		[activeColumnFilters, columnsByField, rows],
	);
	const sortedRows = useMemo(
		() => sortGroupedRows(filteredRows, sortState, columnsByField),
		[columnsByField, filteredRows, sortState],
	);
	const pageCount = useMemo(
		() => Math.ceil(sortedRows.length / ROWS_PER_PAGE),
		[sortedRows.length],
	);

	useEffect(() => {
		if (pageCount === 0 && currentPage !== 0) {
			setCurrentPage(0);
			return;
		}

		if (pageCount > 0 && currentPage > pageCount - 1) {
			setCurrentPage(pageCount - 1);
		}
	}, [currentPage, pageCount]);

	const paginatedRows = useMemo(() => {
		const startIndex = currentPage * ROWS_PER_PAGE;
		return sortedRows.slice(startIndex, startIndex + ROWS_PER_PAGE);
	}, [currentPage, sortedRows]);

	const pageSelectableRows = useMemo(
		() => paginatedRows.filter((row) => isRowSelectable(row)),
		[isRowSelectable, paginatedRows],
	);
	const selectedPageCount = useMemo(
		() => pageSelectableRows.filter((row) => selectedIdsSet.has(row.classid)).length,
		[pageSelectableRows, selectedIdsSet],
	);
	const allPageRowsSelected =
		pageSelectableRows.length > 0 && selectedPageCount === pageSelectableRows.length;
	const somePageRowsSelected = selectedPageCount > 0 && !allPageRowsSelected;

	const handleTogglePageSelection = useCallback(
		(checked) => {
			const nextSelectedIds = new Set(getSelectedIdsFromModel(selectionModel, rows));

			pageSelectableRows.forEach((row) => {
				if (checked) {
					nextSelectedIds.add(row.classid);
				} else {
					nextSelectedIds.delete(row.classid);
				}
			});

			setSelectionModel({
				type: "include",
				ids: nextSelectedIds,
			});
		},
		[pageSelectableRows, rows, selectionModel, setSelectionModel],
	);

	const handleToggleSort = useCallback((field) => {
		setSortState((prev) => {
			if (prev.field === field) {
				return {
					field,
					direction: prev.direction === "asc" ? "desc" : "asc",
				};
			}

			return {
				field,
				direction: "asc",
			};
		});
	}, []);

	const compactCellPaddingClass = isConstrainedLayout ? "px-1 py-1" : "px-3 py-2";
	const pageStart = sortedRows.length === 0 ? 0 : currentPage * ROWS_PER_PAGE + 1;
	const pageEnd = Math.min(sortedRows.length, (currentPage + 1) * ROWS_PER_PAGE);
	const visibleFilteredRowIdsSet = useMemo(
		() => new Set(sortedRows.map((row) => row.classid)),
		[sortedRows],
	);
	const hasAnyActiveFilters =
		Object.keys(activeColumnFilters).length > 0 || Boolean(hasExternalFiltersApplied);
	const hiddenSelectedCount = useMemo(
		() => selectedIds.filter((classid) => !visibleFilteredRowIdsSet.has(classid)).length,
		[selectedIds, visibleFilteredRowIdsSet],
	);
	const activeHeaderColumn = headerMenu?.field ? columnsByField.get(headerMenu.field) : null;
	const activeHeaderFilterField = headerMenu?.targetField ?? headerMenu?.field;
	const activeHeaderColumnFilterValue = activeHeaderColumn
		? getColumnHeaderFilterValue(activeHeaderColumn, activeColumnFilters)
		: "";
	const activeHeaderFilterLabel = activeHeaderColumn
		? getColumnMenuFieldLabel(activeHeaderColumn, activeHeaderFilterField)
		: "";
	const countEvaluateYes = filteredRows.filter((row) => row.evaluate === "Yes").length;
	const countEvaluateNo = filteredRows.filter((row) => row.evaluate === "No").length;
	const countEvaluateUndefined = filteredRows.filter(
		(row) => row.evaluate !== "Yes" && row.evaluate !== "No",
	).length;

	return (
		<div className="relative flex min-h-0 w-full flex-1 flex-col">
			{loading && (
				<div className="absolute left-0 top-0 z-50 flex h-full w-full items-center justify-center bg-gray-200/90">
					<CircularProgress size={100} />
				</div>
			)}

			<div
				className={`flex-1 overflow-auto border border-gray-300 border-t-0 ${
					tableDisabled ? "bg-gray-200" : "bg-white"
				}`}
			>
				{hasAnyActiveFilters && hiddenSelectedCount > 0 ? (
					<div className="border-b border-amber-200 bg-amber-50 px-4 py-2 text-xs text-amber-900">
						<div className="flex items-center justify-between gap-3">
							<p>
								{hiddenSelectedCount} selected row{hiddenSelectedCount === 1 ? "" : "s"}{" "}
								{hiddenSelectedCount === 1 ? "is" : "are"} being filtered out.
							</p>
							<button
								type="button"
								onClick={handleClearAllFilters}
								className="font-semibold text-amber-900 underline"
							>
								Click to clear filters
							</button>
						</div>
					</div>
				) : null}
				<table
					className={`min-w-full border-collapse ${isConstrainedLayout ? "text-[11px]" : "text-xs"}`}
				>
					<thead className="sticky top-0 z-10 bg-white shadow-sm">
						<tr>
							{visibleColumns.map((column) => {
								const isSelectionColumn = column.field === "__select";

								return (
									<th
										key={column.field}
										scope="col"
										aria-sort={
											column.sortable
												? isColumnSortActive(column, sortState)
													? sortState.direction === "asc"
														? "ascending"
														: "descending"
													: "none"
												: undefined
										}
										className={`border-b border-gray-300 bg-white ${compactCellPaddingClass} ${getColumnAlignmentClasses(
											column.headerAlign ?? column.align,
										)}`}
										style={{
											width: column.width,
											minWidth: column.minWidth,
										}}
									>
										{isSelectionColumn ? (
											<div
												className="flex items-center justify-center"
												onClick={(event) => event.stopPropagation()}
											>
												<Checkbox
													size="small"
													disableRipple
													sx={{ p: 0.25 }}
													disabled={selectionDisabled || pageSelectableRows.length === 0}
													checked={allPageRowsSelected}
													indeterminate={somePageRowsSelected}
													onChange={(event) => handleTogglePageSelection(event.target.checked)}
												/>
											</div>
										) : (
											<ColumnHeaderButton
												column={column}
												sortState={sortState}
												filterValue={getColumnHeaderFilterValue(column, activeColumnFilters)}
												onToggleSort={handleToggleSort}
												onOpenMenu={handleOpenHeaderMenu}
												headerIcon={column.headerIcon}
												isConstrainedLayout={isConstrainedLayout}
											/>
										)}
									</th>
								);
							})}
						</tr>
					</thead>
					<tbody>
						{paginatedRows.length === 0 ? (
							<tr>
								<td
									colSpan={visibleColumns.length}
									className="px-4 py-10 text-center text-sm font-medium text-gray-500"
								>
									No classes found.
								</td>
							</tr>
						) : (
							paginatedRows.map((row) => {
								const rowClassName = composedRowClassName(row);
								const isSelected = selectedIdsSet.has(row.classid);
								const rowIsClickable = hasBulkSelection
									? !selectionDisabled && isRowSelectable(row)
									: !selectionDisabled;
								const rowPresentation = getRowPresentation({
									rowClassName,
									theme,
									isSelected,
									isClickable: rowIsClickable,
								});

								return (
									<tr
										key={row.classid}
										className={rowPresentation.className}
										style={rowPresentation.style}
										onClick={(event) => handleRowClick(row, event)}
										onContextMenu={(event) => handleRowContextMenu(row, event)}
									>
										{visibleColumns.map((column) => (
											<td
												key={`${row.classid}-${column.field}`}
												className={`border-b border-gray-200 align-middle ${compactCellPaddingClass} ${getColumnAlignmentClasses(
													column.align,
												)}`}
												style={{
													width: column.width,
													minWidth: column.minWidth,
												}}
											>
												{column.renderCell ? column.renderCell(row) : (row[column.field] ?? "")}
											</td>
										))}
									</tr>
								);
							})
						)}
					</tbody>
				</table>
			</div>

			<TablePaginationControls
				countEvaluateYes={countEvaluateYes}
				countEvaluateNo={countEvaluateNo}
				countEvaluateUndefined={countEvaluateUndefined}
				currentPage={currentPage}
				pageCount={pageCount}
				rowCount={sortedRows.length}
				pageStart={pageStart}
				pageEnd={pageEnd}
				onPrevious={() => setCurrentPage((prev) => Math.max(prev - 1, 0))}
				onNext={() =>
					setCurrentPage((prev) => (pageCount === 0 ? 0 : Math.min(prev + 1, pageCount - 1)))
				}
			/>

			<Menu
				open={Boolean(headerMenu)}
				onClose={handleCloseHeaderMenu}
				anchorEl={headerMenu?.anchorEl ?? null}
				anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
				transformOrigin={{ vertical: "top", horizontal: "left" }}
				MenuListProps={{ dense: true }}
				sx={{ opacity: "95%" }}
			>
				{headerMenu?.pendingAction && columnUsesMenuFieldOptions(activeHeaderColumn) ? (
					<DateFieldTargetMenu
						column={activeHeaderColumn}
						action={headerMenu.pendingAction}
						activeColumnFilters={activeColumnFilters}
						onBack={handleBackToHeaderMenuActions}
						onChooseField={handleChooseHeaderMenuField}
					/>
				) : (
					<>
						<MenuItem
							onClick={() => {
								if (columnUsesMenuFieldOptions(activeHeaderColumn)) {
									handleStartHeaderMenuAction("sortAsc");
									return;
								}

								handleSetSort(headerMenu?.field, "asc");
							}}
							disabled={!activeHeaderColumn?.sortable}
						>
							Sort ASC
						</MenuItem>
						<MenuItem
							onClick={() => {
								if (columnUsesMenuFieldOptions(activeHeaderColumn)) {
									handleStartHeaderMenuAction("sortDesc");
									return;
								}

								handleSetSort(headerMenu?.field, "desc");
							}}
							disabled={!activeHeaderColumn?.sortable}
						>
							Sort DESC
						</MenuItem>
						<MenuItem
							onClick={() => {
								if (columnUsesMenuFieldOptions(activeHeaderColumn)) {
									handleStartHeaderMenuAction("filter");
									return;
								}

								handleOpenColumnFilterEditor();
							}}
							disabled={!activeHeaderColumn?.filterable}
						>
							<div className="flex w-full items-center justify-between gap-3">
								<span>
									{activeHeaderColumnFilterValue
										? `Filter "${activeHeaderColumnFilterValue}"`
										: "Filter"}
								</span>
								{activeHeaderColumnFilterValue ? (
									<button
										type="button"
										onClick={(event) => {
											event.stopPropagation();
											handleClearHeaderColumnFilters(activeHeaderColumn);
										}}
										className="text-xs font-semibold text-red-700"
									>
										Clear
									</button>
								) : null}
							</div>
						</MenuItem>
					</>
				)}
				{headerMenu?.isFilterEditorOpen ? (
					<div className="px-3 pb-3 pt-1" onClick={(event) => event.stopPropagation()}>
						<TextField
							autoFocus
							inputRef={filterInputRef}
							fullWidth
							size="small"
							label={`Filter ${activeHeaderFilterLabel}`}
							value={headerMenu.draftFilterValue}
							onChange={(event) => {
								const nextValue = event.target.value;
								handleHeaderMenuDraftChange(nextValue);
								if (activeHeaderFilterField) {
									commitColumnFilter(activeHeaderFilterField, nextValue);
								}
							}}
							onKeyDown={(event) => {
								event.stopPropagation();

								if (event.key === "Enter") {
									event.preventDefault();
									if (activeHeaderFilterField) {
										commitColumnFilter(activeHeaderFilterField, headerMenu.draftFilterValue);
									}
									setHeaderMenu(null);
								}

								if (event.key === "Escape") {
									event.preventDefault();
									setHeaderMenu(null);
								}
							}}
						/>
					</div>
				) : null}
			</Menu>

			<Menu
				open={Boolean(rowActionMenu)}
				onClose={handleCloseRowActionMenu}
				anchorReference="anchorPosition"
				anchorPosition={
					rowActionMenu ? { top: rowActionMenu.mouseY, left: rowActionMenu.mouseX } : undefined
				}
				transformOrigin={{ vertical: "top", horizontal: "left" }}
				MenuListProps={{ dense: true }}
			>
				<MenuItem
					onClick={() => {
						if (!rowActionMenu?.row) {
							return;
						}

						handleCloseRowActionMenu();
						handleOpenSelectedClassModal(rowActionMenu.row);
					}}
					disabled={singleClassEditingDisabled}
					className="flex gap-2 items-center"
				>
					<BuildIcon /> Manage Class
				</MenuItem>

				<MenuItem
					onClick={() => {
						if (!rowActionMenu?.row) {
							return;
						}

						handleCloseRowActionMenu();
						handleOpenAddInstancedClassFunction(rowActionMenu.row);
					}}
					disabled={singleClassEditingDisabled}
					className="flex gap-2 items-center"
				>
					<ContentCopyIcon /> Instanced Classes
				</MenuItem>
				<MenuItem
					onClick={() => handleAddRowToBulkSelection(rowActionMenu?.row)}
					disabled={!rowActionMenu?.row || !isRowSelectable(rowActionMenu.row)}
				>
					Add Class To Bulk Selection
				</MenuItem>
			</Menu>
		</div>
	);
};

export default DataGridTable;
